import { Box, Flex } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ToastContainer } from 'react-toastify'

import { useAuth } from '@/auth/useAuth'
import { SkipLink } from '@/components/atoms/SkipLink/SkipLink'
import { Footer } from '@/components/molecules/Footer/Footer'
import { ContextSwitchOverlay } from '@/components/molecules/Menu/ContextSwitchOverlay'
import { HeadBar } from '@/components/molecules/Menu/HeadBar'
import { MobileBottomNav } from '@/components/molecules/Menu/MobileBottomNav'
import { useColorMode } from '@/components/ui/color-mode'
import { SearchProvider } from '@/contexts/Circles/SearchContext'
import { useBootstrapApiReachabilityProbe } from '@/hooks/useBootstrapApiReachabilityProbe'
import { useContext } from '@/hooks/useContext'
import { useContextSwitchDispatcher } from '@/hooks/useContextSwitchDispatcher'
import { useSwitchContext } from '@/hooks/useSwitchContext'
import { Outlet, useLocation } from '@/routing'
import { CONTEXT_CHANGED_EVENT } from '@/utils/contextChangedEvent'

const favicon = '/favicon.svg'

export const Layout = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const { user } = auth

  useBootstrapApiReachabilityProbe()

  // Sentry Feedback Configuration
  // The trigger button is hidden by default and opened via the sidebar support button
  useEffect(() => {
    Sentry.addIntegration(
      Sentry.feedbackIntegration({
        colorScheme: auth.user?.settings?.themeMode ?? 'dark',
        showBranding: false,
        showName: true,
        showEmail: true,
        autoInject: false, // Hide the default trigger button, use sidebar instead
        useSentryUser: {
          name: 'fullname',
          email: 'email',
        },
        triggerLabel: t('feedback.triggerLabel'),
        triggerAriaLabel: t('feedback.triggerAriaLabel'),
        formTitle: t('feedback.formTitle'),
        submitButtonLabel: t('feedback.submitButtonLabel'),
        cancelButtonLabel: t('feedback.cancelButtonLabel'),
        confirmButtonLabel: t('feedback.confirmButtonLabel'),
        addScreenshotButtonLabel: t('feedback.addScreenshotButtonLabel'),
        removeScreenshotButtonLabel: t('feedback.removeScreenshotButtonLabel'),
        nameLabel: t('feedback.nameLabel'),
        namePlaceholder: t('feedback.namePlaceholder'),
        emailLabel: t('feedback.emailLabel'),
        emailPlaceholder: t('feedback.emailPlaceholder'),
        isRequiredLabel: t('feedback.isRequiredLabel'),
        messageLabel: t('feedback.messageLabel'),
        messagePlaceholder: t('feedback.messagePlaceholder'),
        successMessageText: t('feedback.successMessageText'),
      })
    )
  }, [auth.user?.settings?.themeMode, t])

  const location = useLocation()
  const { contextIri } = useContext()
  const { colorMode } = useColorMode()
  const { isSwitching, isImpersonationExitOverlay } = useSwitchContext()
  const { dispatchContextSwitchNavigation } = useContextSwitchDispatcher()
  const [switchingTo, setSwitchingTo] = useState<string | null>(null)

  useEffect(() => {
    const handleContextChange = () => {
      if (isSwitching) {
        setSwitchingTo(localStorage.getItem('switchingTo'))
      } else {
        setSwitchingTo(null)
      }
    }

    handleContextChange()

    window.addEventListener(CONTEXT_CHANGED_EVENT, handleContextChange)
    return () => {
      window.removeEventListener(CONTEXT_CHANGED_EVENT, handleContextChange)
    }
  }, [isSwitching])

  useEffect(() => {
    if (!contextIri || isSwitching) {
      return
    }

    const isContextualOrMyRoute =
      location.pathname.startsWith('/n/') ||
      location.pathname.startsWith('/c/') ||
      location.pathname.startsWith('/my/')

    if (!isContextualOrMyRoute) {
      return
    }

    dispatchContextSwitchNavigation(contextIri)
  }, [
    contextIri,
    dispatchContextSwitchNavigation,
    isSwitching,
    location.pathname,
  ])

  return (
    <>
      <meta charSet="utf-8" />
      <link rel="icon" type="image/svg+xml" href={favicon} />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/favicon/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon/favicon-16x16.png"
      />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      <link
        rel="mask-icon"
        href="/favicon/safari-pinned-tab.svg"
        color="#5bbad5"
      />
      <meta name="msapplication-TileColor" content="#da532c" />
      <meta name="theme-color" content="#ffde59" />
      <ContextSwitchOverlay
        isVisible={isSwitching || isImpersonationExitOverlay}
        variant={isImpersonationExitOverlay ? 'impersonation' : 'context'}
        newContextName={switchingTo}
      />
      <SearchProvider>
        <Flex direction="column" flex="1" bg="bg.canvas">
          <SkipLink />
          <HeadBar />
          <Flex
            as="main"
            id="main"
            // Focus target for the skip link. Programmatic focus only (not a Tab
            // stop), so the region outline is suppressed — the user jumped here
            // intentionally and an outline around the whole page is unhelpful.
            tabIndex={-1}
            outline="none"
            direction="column"
            flex="1"
            mt={{ base: 20, md: 20 }}
            pb={{
              base: user ? 'calc(92px + env(safe-area-inset-bottom))' : '0',
              md: '0',
            }}
          >
            <Box minH="lg">
              <Outlet />
            </Box>
          </Flex>
          <ToastContainer
            theme={colorMode as 'dark' | 'light'}
            draggablePercent={60}
            role="alert"
            newestOnTop
            position="bottom-right"
            style={{
              marginBottom: user
                ? 'calc(96px + env(safe-area-inset-bottom))'
                : 80,
            }}
          />
          <Footer />
          {/* Mobile bottom navigation - only show when user is logged in */}
          {user && <MobileBottomNav />}
        </Flex>
      </SearchProvider>
    </>
  )
}
